import React, { useCallback, useEffect, useRef, useState } from 'react';
import ThreadList from '../components/messaging/ThreadList';
import ThreadDetail from '../components/messaging/ThreadDetail';
import { ChatThread } from '../types/chat';
import {
  chatWebSocket,
  chatService,
  StaffChatMessage,
  StaffChatThread,
} from '../services/chatService';
import { fetchHotels } from '../services/hotelAdminService';
import { apiErrorMessage } from '../utils/apiError';

function staffRoleLabel(role: string): string {
  switch (role) {
    case 'ont':
      return 'ONT';
    case 'anr':
      return 'ANR';
    case 'admin':
      return 'Admin';
    case 'cns':
      return 'CNS';
    default:
      return role.toUpperCase();
  }
}

const MessagesPage: React.FC = () => {
  const [selectedThread, setSelectedThread] = useState<ChatThread | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showHotelPicker, setShowHotelPicker] = useState(false);
  const [hotelSearch, setHotelSearch] = useState('');
  const [hotels, setHotels] = useState<Array<{ id: string; name: string; city?: string; province?: string }>>([]);
  const [hotelsLoading, setHotelsLoading] = useState(false);

  // Agency staff chat (CNS ↔ ONT / Admin) — does not use hotel threads
  const [staffThread, setStaffThread] = useState<StaffChatThread | null>(null);
  const [staffMessages, setStaffMessages] = useState<StaffChatMessage[]>([]);
  const [staffLabel, setStaffLabel] = useState('');
  const [staffContent, setStaffContent] = useState('');
  const [staffSending, setStaffSending] = useState(false);
  const staffThreadIdRef = useRef<string | undefined>();
  staffThreadIdRef.current = staffThread?.id;

  useEffect(() => {
    chatWebSocket.connect();
    const unsubscribe = chatWebSocket.onMessage((message) => {
      setRefreshKey((prev) => prev + 1);
      setSelectedThread((prev) =>
        prev && prev.id === message.thread_id ? { ...prev, updated_at: message.created_at } : prev
      );
    });
    const unsubscribeStaff = chatWebSocket.onStaffMessage((message) => {
      if (message.thread_id === staffThreadIdRef.current) {
        setStaffMessages((prev) =>
          prev.some((m) => m.id === message.id) ? prev : [...prev, message]
        );
      }
    });

    return () => {
      unsubscribe();
      unsubscribeStaff();
    };
  }, []);

  const handleSelectThread = (thread: ChatThread) => {
    setStaffThread(null);
    setStaffMessages([]);
    setStaffLabel('');
    setSelectedThread(thread);
  };

  const handleSelectStaffThread = async (thread: StaffChatThread, label: string) => {
    try {
      setSelectedThread(null);
      setStaffLabel(label);
      setStaffThread(thread);
      const messages = await chatService.getStaffMessages(thread.id);
      setStaffMessages(Array.isArray(messages) ? messages : []);
    } catch (err: unknown) {
      console.error('Staff open error:', err);
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      alert(apiErrorMessage(data, 'Impossible d ouvrir la conversation interne'));
    }
  };

  const handleThreadUpdate = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const loadHotels = useCallback(async (query: string = '') => {
    setHotelsLoading(true);
    try {
      // Full hotel registry so CNS can start conversations even with an empty inbox.
      const page = await fetchHotels({ limit: 200, search: query || undefined });
      const list = Array.isArray(page?.data)
        ? page.data
        : Array.isArray((page as unknown as { hotels?: unknown[] })?.hotels)
          ? (page as unknown as { hotels: Array<{ id: string; name?: string; city?: string; province?: string }> }).hotels
          : Array.isArray(page)
            ? (page as Array<{ id: string; name?: string; city?: string; province?: string }>)
            : [];
      const mapped = list.map((h) => ({
        id: h.id,
        name: h.name || `Hotel ${String(h.id).slice(0, 8)}`,
        city: h.city,
        province: h.province,
      }));
      if (mapped.length > 0) {
        setHotels(mapped);
        return;
      }
      // Fallback: hotels already present in CNS threads
      const threads = await chatService.getThreads({});
      const uniqueHotels = new Map<string, { id: string; name: string; city?: string; province?: string }>();
      threads.forEach((t) => {
        if (t.hotel_id && !uniqueHotels.has(t.hotel_id)) {
          uniqueHotels.set(t.hotel_id, {
            id: t.hotel_id,
            name: t.hotel_name || `Hotel ${t.hotel_id.slice(0, 8)}`,
          });
        }
      });
      setHotels(
        Array.from(uniqueHotels.values()).filter(
          (h) => !query || h.name.toLowerCase().includes(query.toLowerCase())
        )
      );
    } catch {
      setHotels([]);
    } finally {
      setHotelsLoading(false);
    }
  }, []);

  const startConversationWithHotel = async (hotel: { id: string; name: string }) => {
    try {
      // CNS desk owns the thread (recipient_type=cns) so it appears in the CNS inbox.
      const thread = await chatService.createThread(hotel.id, 'cns');
      setShowHotelPicker(false);
      setStaffThread(null);
      setStaffMessages([]);
      setStaffLabel('');
      setRefreshKey((p) => p + 1);
      setSelectedThread(thread);
    } catch (err: unknown) {
      console.error('Error creating thread:', err);
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      alert(apiErrorMessage(data, 'Erreur lors de la creation de la conversation'));
    }
  };

  const openStaffChannel = async (recipientRole: 'ont' | 'anr' | 'admin', label: string) => {
    try {
      const thread = await chatService.createStaffThread(recipientRole);
      setSelectedThread(null);
      setStaffLabel(label);
      setStaffThread(thread);
      setRefreshKey((p) => p + 1);
      const messages = await chatService.getStaffMessages(thread.id);
      setStaffMessages(Array.isArray(messages) ? messages : []);
    } catch (err: unknown) {
      console.error('Staff channel error:', err);
      const data = (err as { response?: { data?: unknown } })?.response?.data;
      alert(apiErrorMessage(data, 'Impossible d ouvrir la conversation'));
    }
  };

  const sendStaff = async () => {
    if (!staffThread || staffContent.trim().length < 10) {
      if (staffContent.trim().length > 0 && staffContent.trim().length < 10) {
        alert('Le message doit contenir au moins 10 caracteres');
      }
      return;
    }
    setStaffSending(true);
    try {
      const message = await chatService.sendStaffMessage(staffThread.id, staffContent.trim());
      setStaffMessages((prev) =>
        prev.some((m) => m.id === message.id) ? prev : [...prev, message]
      );
      setStaffContent('');
    } catch (err) {
      console.error(err);
      alert('Erreur lors de l envoi du message');
    } finally {
      setStaffSending(false);
    }
  };

  const formatTime = (value: string) => {
    if (!value) return '';
    return new Date(value).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    // Fill remaining viewport under Navigation + Breadcrumb (not full 100vh — that clipped the composer).
    <div className="flex h-[calc(100vh-7.5rem)] min-h-[28rem] overflow-hidden bg-white">
      {showHotelPicker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setShowHotelPicker(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Contacter un hotel</h3>
              <button
                onClick={() => setShowHotelPicker(false)}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                &times;
              </button>
            </div>
            <div className="px-5 py-3 border-b">
              <input
                value={hotelSearch}
                onChange={(e) => {
                  setHotelSearch(e.target.value);
                  loadHotels(e.target.value);
                }}
                placeholder="Rechercher par nom..."
                className="w-full border rounded px-3 py-2 text-sm"
                autoFocus
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {hotelsLoading ? (
                <div className="text-center py-8 text-gray-500">Chargement...</div>
              ) : hotels.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">Aucun hotel trouve</div>
              ) : (
                hotels.map((hotel) => (
                  <button
                    key={hotel.id}
                    onClick={() => startConversationWithHotel(hotel)}
                    className="w-full text-left px-5 py-3 border-b hover:bg-blue-50 transition-colors"
                  >
                    <div className="font-medium text-gray-900 text-sm">{hotel.name}</div>
                    {hotel.city && (
                      <div className="text-xs text-gray-500">
                        {hotel.city}
                        {hotel.province && `, ${hotel.province}`}
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="w-96 flex-shrink-0 flex flex-col min-h-0 border-r">
        <div className="p-3 border-b space-y-2 flex-shrink-0">
          <button
            onClick={() => {
              setShowHotelPicker(true);
              loadHotels();
            }}
            className="w-full flex items-center justify-center gap-2 rounded bg-blue-700 px-3 py-2 text-sm font-medium text-white hover:bg-blue-800"
          >
            + Contacter un Hotel
          </button>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => openStaffChannel('ont', 'ONT — Office National du Tourisme')}
              className="flex-1 min-w-[30%] rounded bg-indigo-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-indigo-700"
            >
              Contacter ONT
            </button>
            <button
              onClick={() => openStaffChannel('anr', 'ANR — Agence Nationale de Renseignements')}
              className="flex-1 min-w-[30%] rounded bg-emerald-700 px-2 py-1.5 text-xs font-medium text-white hover:bg-emerald-800"
            >
              Contacter ANR
            </button>
            <button
              onClick={() => openStaffChannel('admin', 'Support plateforme')}
              className="flex-1 min-w-[30%] rounded bg-green-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-green-700"
            >
              Support
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <ThreadList
            refreshKey={refreshKey}
            onSelectThread={handleSelectThread}
            selectedThreadId={selectedThread?.id}
            onSelectStaffThread={handleSelectStaffThread}
            selectedStaffThreadId={staffThread?.id}
          />
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {staffThread ? (
          <>
            <header className="bg-indigo-700 text-white px-6 py-4 flex-shrink-0">
              <h2 className="text-lg font-semibold">{staffLabel || 'Conversation interne'}</h2>
              <p className="text-sm text-indigo-100 mt-1">
                Canal sécurisé inter-agences (CNS ↔ ONT / ANR / Support)
              </p>
            </header>
            <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-3 bg-gray-50">
              {staffMessages.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-12">
                  Aucun message. Envoyez le premier message à {staffLabel || 'votre correspondant'}.
                </p>
              ) : (
                staffMessages.map((message) => {
                  const own = message.sender_role === 'cns';
                  return (
                    <div key={message.id} className={`flex ${own ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[70%] rounded-lg px-4 py-2 shadow-sm ${
                          own ? 'bg-blue-600 text-white' : 'bg-white text-gray-900 border'
                        }`}
                      >
                        <div className={`text-xs mb-1 ${own ? 'text-blue-100' : 'text-gray-500'}`}>
                          {staffRoleLabel(message.sender_role)}
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        <div className={`text-xs mt-1 ${own ? 'text-blue-100' : 'text-gray-400'}`}>
                          {formatTime(message.created_at)}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <footer className="p-4 border-t bg-white flex-shrink-0">
              <div className="flex gap-2 items-end">
                <textarea
                  value={staffContent}
                  onChange={(e) => setStaffContent(e.target.value)}
                  className="flex-1 border rounded px-3 py-2 text-sm resize-none"
                  rows={2}
                  placeholder="Ecrire un message (min. 10 caracteres)..."
                />
                <button
                  onClick={sendStaff}
                  disabled={staffSending || staffContent.trim().length === 0}
                  className="px-4 py-2 bg-indigo-600 text-white rounded text-sm disabled:opacity-50"
                >
                  Envoyer
                </button>
              </div>
            </footer>
          </>
        ) : selectedThread ? (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <ThreadDetail thread={selectedThread} onThreadUpdate={handleThreadUpdate} />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center flex-1 min-h-0 bg-gray-50 text-gray-500 px-6">
            <svg className="w-24 h-24 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <h2 className="text-xl font-semibold mb-2 text-gray-700">Selectionnez une conversation</h2>
            <p className="text-center max-w-md">
              Choisissez une conversation dans la liste de gauche, ou démarrez un échange avec un hôtel,
              l&apos;ONT, l&apos;ANR ou le support plateforme.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-center">
              <button
                onClick={() => {
                  setShowHotelPicker(true);
                  loadHotels();
                }}
                className="rounded bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
              >
                Contacter un Hôtel
              </button>
              <button
                onClick={() => openStaffChannel('ont', 'ONT — Office National du Tourisme')}
                className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Contacter ONT
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
